import { useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useVault } from "@/context/VaultContext";

export function ConnectDialog({ open, onOpenChange }) {
  const { profile, addContact, setActiveSid } = useVault();
  const [code, setCode] = useState("");
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const copy = async () => {
    await navigator.clipboard.writeText(profile.code).catch(() => {});
    toast.success("Aphelion ID copied");
  };

  const connect = async () => {
    setBusy(true);
    setError("");
    try {
      const sid = await addContact(code, nick.trim());
      setCode(""); setNick("");
      onOpenChange(false);
      setActiveSid(sid);
      toast.success("Handshake sent");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connect-dialog" className="bg-nebula border-rim text-ash sm:max-w-md rounded-secure">
        <DialogHeader>
          <DialogTitle className="font-branding">Connect via key</DialogTitle>
          <DialogDescription className="text-ash/50">Exchange Aphelion IDs out-of-band. No directory, no phone numbers.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="mine">
          <TabsList className="grid grid-cols-2 bg-void rounded-secure">
            <TabsTrigger value="mine" data-testid="connect-tab-mine" className="rounded-secure data-[state=active]:bg-nebula data-[state=active]:text-stellar">My ID</TabsTrigger>
            <TabsTrigger value="add" data-testid="connect-tab-add" className="rounded-secure data-[state=active]:bg-nebula data-[state=active]:text-stellar">Add contact</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="space-y-4 pt-3">
            <div className="flex justify-center p-4 bg-void rounded-secure border border-glass" data-testid="my-id-qr">
              <QRCodeSVG value={profile.code} size={176} bgColor="#050507" fgColor="#0AE2FF" level="M" />
            </div>
            <p className="font-mono text-[11px] break-all bg-void border border-glass rounded-secure p-3 text-ash/70" data-testid="my-aphelion-id">{profile.code}</p>
            <button onClick={copy} data-testid="copy-id-btn" className="w-full flex items-center justify-center gap-2 border border-stellar/40 text-stellar rounded-secure py-2.5 text-sm hover:shadow-stellar-glow transition-shadow"><Copy size={16} strokeWidth={1.5} /> Copy ID</button>
          </TabsContent>
          <TabsContent value="add" className="space-y-3 pt-3">
            <textarea data-testid="connect-code-input" value={code} onChange={(e) => setCode(e.target.value)} rows={3} placeholder="aph1…"
              className="w-full bg-void border border-rim rounded-secure p-3 font-mono text-xs text-ash placeholder-ash/30 focus:outline-none focus:border-stellar/50 resize-none" />
            <input data-testid="connect-nickname-input" value={nick} onChange={(e) => setNick(e.target.value)} placeholder="Nickname (optional, stored only on this device)"
              className="w-full bg-void border border-rim rounded-secure px-3 py-2.5 text-sm placeholder-ash/30 focus:outline-none focus:border-stellar/50" />
            {error && <p className="text-sm text-flare" data-testid="connect-error">{error}</p>}
            <button onClick={connect} disabled={busy || !code.trim()} data-testid="connect-submit-btn" className="w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-2.5 disabled:opacity-50">
              {busy && <Loader2 size={16} className="animate-spin" />} Initiate handshake
            </button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
